import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Localization} from '../../../../projects/common/src/lib/interface/localization.extension';
import {GuestLinkApiService} from '../../common/services/api/routes/guest-link-api.service';
import {AuthGuardService} from '../../common/services/auth-guard.service';

/** What the visitor last started from a given link, remembered in this browser. */
interface RememberedProject {
  projectId: string;
  at: number;
}

type Phase = 'starting' | 'returning' | 'error';

/**
 * /g/<id>: an Open Guest owner's public link.
 *
 * A visitor lands here from the owner's website and is given a project of
 * their own to measure in. The page exists rather than a route guard because a
 * guard can only redirect or hang, and the old guest-link guard did hang: it
 * returned from inside a Promise executor, which resolves nothing, so any
 * failure left a blank page -- the one moment a visitor most needs telling
 * what happened.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'app-guest-start-page',
  templateUrl: 'guest-start.page.html',
  styleUrls: ['guest-start.page.scss'],
})
export class GuestStartPage extends Localization implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(GuestLinkApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly authGuard = inject(AuthGuardService);

  /** How long a remembered project is offered back before we just start fresh. */
  private static readonly REMEMBER_FOR = 60 * 24 * 60 * 60 * 1000;

  phase: Phase = 'starting';
  errorTitleKey = '';
  errorBodyKey = '';
  /** Outlined font: base names only (`error`, not `error_outline`). */
  errorIcon = 'error';
  /** Paused, busy or unavailable is the business's state, not a fault: no red. */
  errorIsFault = true;
  canRetry = false;

  private linkId = '';
  private remembered: RememberedProject | null = null;

  ngOnInit() {
    // No auth guard runs on this route, and it is the guard that normally
    // lifts the app's start-up splash -- left up, it hides this page entirely.
    this.authGuard.loader.set(false);
    this.authGuard.loader$.next(false);
    this.linkId = this.route.snapshot.paramMap.get('linkId') || '';
    if (!this.linkId) {
      // /guest/unavailable: the integrator's token flow sends its failures
      // here with the reason, so there is nothing to start -- only to explain.
      this.showError({msg: {errorCode: this.route.snapshot.queryParamMap.get('code') || ''}});
      this.canRetry = false;
      return;
    }
    this.remembered = this.readRemembered();
    // Coming back to the same business's link usually means "my project", not
    // "another one" -- and every new one is billed to the owner. So ask.
    if (this.remembered) {
      this.phase = 'returning';
    } else {
      this.start();
    }
  }

  start() {
    this.phase = 'starting';
    this.cdr.detectChanges();
    const name = `${this.strings['guest_project_name'] || ''} ${new Date().toLocaleDateString()}`.trim();
    this.api.startProject(this.linkId, {
      name,
      source: document.referrer || '',
    }).then(result => {
      this.remember(result.projectId);
      this.open(result.projectId);
    }).catch(err => this.showError(err));
  }

  continueProject() {
    if (this.remembered) {
      this.open(this.remembered.projectId);
    }
  }

  /**
   * A full load rather than a router hop: the canvas signs a visitor in
   * anonymously as it opens, and it should start clean. `replace`, so that
   * Back leaves for the site the visitor came from instead of landing here
   * again and starting -- and billing -- another project.
   */
  private open(projectId: string) {
    window.location.replace(`/app/p/${encodeURIComponent(projectId)}`);
  }

  private showError(err: any) {
    const code = err && err.msg && err.msg.errorCode;
    this.canRetry = false;
    this.errorIsFault = code !== 'guest_link_paused' && code !== 'invalid_subscription'
      && code !== 'budget_limit_reached' && code !== 'too_many_requests' && code !== 'api_key_suspended';
    switch (code) {
      case 'guest_link_not_found':
        this.errorTitleKey = 'guest_link_error_not_found_title';
        this.errorBodyKey = 'guest_link_error_not_found';
        this.errorIcon = 'link_off';
        break;
      case 'guest_link_paused':
        this.errorTitleKey = 'guest_link_error_paused_title';
        this.errorBodyKey = 'guest_link_error_paused';
        this.errorIcon = 'pause_circle';
        break;
      // A lapsed subscription and a reached budget read the same to a visitor:
      // the business cannot take a new project just now. The budget is the
      // owner's business, not theirs.
      case 'invalid_subscription':
      case 'budget_limit_reached':
      // The owner's API access is suspended: also not the visitor's business.
      case 'api_key_suspended':
        this.errorTitleKey = 'guest_link_error_unavailable_title';
        this.errorBodyKey = 'guest_link_error_unavailable';
        this.errorIcon = 'schedule';
        break;
      case 'too_many_requests':
        this.errorTitleKey = 'guest_link_error_busy_title';
        this.errorBodyKey = 'guest_link_error_busy';
        this.errorIcon = 'hourglass_empty';
        this.canRetry = true;
        break;
      default:
        this.errorTitleKey = 'guest_link_error_generic_title';
        this.errorBodyKey = 'guest_link_error_generic';
        this.errorIcon = 'error';
        this.canRetry = true;
    }
    this.phase = 'error';
    this.cdr.detectChanges();
  }

  private storageKey() {
    return `mm.guestProject.${this.linkId}`;
  }

  private readRemembered(): RememberedProject | null {
    try {
      const raw = localStorage.getItem(this.storageKey());
      if (!raw) {
        return null;
      }
      const value = JSON.parse(raw) as RememberedProject;
      if (!value || !value.projectId || Date.now() - value.at > GuestStartPage.REMEMBER_FOR) {
        return null;
      }
      return value;
    } catch (e) {
      // Storage can be off (private windows, strict embeds). Then every visit
      // simply starts fresh, which is still correct.
      return null;
    }
  }

  private remember(projectId: string) {
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify({projectId, at: Date.now()}));
    } catch (e) {
      // see readRemembered
    }
  }
}
